import { useState, useEffect } from "react";
import { getDatabase, ref, get, update, push } from "firebase/database";
import { onAuthStateChanged, getAuth } from "firebase/auth";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./userNegotiations.css";
import InfoIcon from "./infoIcon";
import { db, set } from "../Firebase/firestore";

const UserNegotiations = () => {
  const [negotiations, setNegotiations] = useState([]);
  const [supplierResponses, setSupplierResponses] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const brand = localStorage.getItem("supplierBrand");
  const authInstance = getAuth();
  const gifUrl = "/Images/New notification.gif";

  const sendNotification = async (userId, negotiationId, title, message, siteaddress) => {
    const notificationRef = ref(db, `user_notifications/Negotiation Response/${userId}`);
    const newNotificationRef = push(notificationRef); // Generate unique ID for notification
    const newNotification = {
      title,
      message,
      gifUrl,
      timestamp: new Date().toISOString(),
      seen: false,
    };

    try {
      await set(newNotificationRef, newNotification);
      console.log("Notification sent successfully");
    } catch (error) {
      console.error("Error sending notification:", error);
    }
  };

  useEffect(() => {
    const checkAuthState = () => {
      setIsLoading(true);
      setError("");

      const unsubscribe = onAuthStateChanged(authInstance, async (user) => {
        if (user) {
          if (!brand) {
            setError("Brand not selected. Please log in again.");
          } else {
            await fetchNegotiations(brand);
          }
        } else {
          setError("User not authenticated. Please log in again.");
        }
        setIsLoading(false);
      });

      return () => unsubscribe();
    };

    checkAuthState();
  }, [brand]);

  const fetchNegotiations = async (selectedBrand) => {
    try {
      const db = getDatabase();
      const negotiationsRef = ref(db, "negotiations");
      const snapshot = await get(negotiationsRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        const filteredNegotiations = Object.entries(data)
          .flatMap(([userId, userNegotiations]) =>
            Object.entries(userNegotiations[selectedBrand] || {})
              .filter(([, negotiation]) => !negotiation.response) // Exclude responded negotiations
              .map(([negotiationId, negotiation]) => ({
                userId,
                negotiationId,
                ...negotiation,
              }))
          );

        console.log("Filtered negotiations:", filteredNegotiations);

        setNegotiations(filteredNegotiations);
        setError("");
      } else {
        setError("No negotiations found for this brand.");
      }
    } catch (err) {
      setError("Error fetching negotiations: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (negotiationId, field, value) => {
    setSupplierResponses((prev) => ({
      ...prev,
      [negotiationId]: {
        ...prev[negotiationId],
        [field]: value,
      },
    }));
  };

  const handleSendResponse = async (negotiation) => {
    const response = supplierResponses[negotiation.negotiationId] || {};

    // Validate inputs before sending
    if (!response.offeredPrice || !response.deliveryTime) {
      setError("Please provide the offered price and delivery time.");
      return;
    }

    try {
      const db = getDatabase();
      const supplierResponse = {
        offeredPrice: response.offeredPrice,
        deliveryTime: response.deliveryTime,
        responseStatus: response.responseStatus,
        timestamp: new Date().toISOString(), // Update with current timestamp
      };

      const responseRef = ref(
        db,
        `negotiations/${negotiation.userId}/${brand}/${negotiation.negotiationId}/response`
      );

      // Update the response in the database
      await update(responseRef, supplierResponse);

      // Send notification after successful response update
      const notificationMessage = `Your negotiation response has been sent for site address: ${negotiation.siteaddress}`;
      await sendNotification(
        negotiation.userId,
        negotiation.negotiationId,
        "Negotiation Response",
        notificationMessage,
        negotiation.siteaddress
      );

      // Reset the response fields
      setSupplierResponses((prev) => ({
        ...prev,
        [negotiation.negotiationId]: {},
      }));
      setSuccessMessage("Response sent successfully!");

      // Refresh negotiations after sending a response
      await fetchNegotiations(brand);
    } catch (err) {
      console.error("Error sending response:", err);
      setError("Failed to send the response. Please try again.");
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Slider settings
  const sliderSettings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
  };

  if (isLoading) return <p>Loading...</p>;
  if (error) return <p className="error-message">{error}</p>;
  if (successMessage) return <p className="success-message">{successMessage}</p>;

  return (
    <div className="negotiations-container">
       <div className="flex items-center space-x-2">
      <h1 className="text-xl font-semibold">User Negotiations <InfoIcon text="This section allows you to manage and respond to user negotiations efficiently. Customers initiate negotiations for better pricing and terms on specific brands and products, providing details such as quantity, preferred grade, and site address. As a supplier, you can review these negotiation requests, input your offered price, delivery time, and response status, and send your response directly to the customer. Users receive notifications once their negotiations are addressed, ensuring transparent and effective communication. This helps in building stronger business relationships and improving customer satisfaction." /></h1>
     </div>
      {negotiations.length === 0 ? (
        <p>No negotiations found for this brand.</p>
      ) : (
        <Slider {...sliderSettings}>
          {negotiations.map((negotiation) => (
            <div className="negotiation-card" key={negotiation.negotiationId}>
              <div><strong>Company Name:</strong> {negotiation.companyName}</div>
              <div><strong>Brand:</strong> {negotiation.brandName}</div>
              <div><strong>Quantity Required:</strong> {negotiation.quantityRequired}</div>
              <div><strong>Preferred Grade:</strong> {negotiation.preferredGrade}</div>
              <div><strong>Site Address:</strong> {negotiation.siteaddress}</div>
              <div><strong>Time Zone:</strong> {formatTimestamp(negotiation.timestamp)}</div>
              <div className="response-fields">
                <input
                  type="text"
                  placeholder="Offered Price"
                  value={supplierResponses[negotiation.negotiationId]?.offeredPrice || ""}
                  onChange={(e) =>
                    handleInputChange(
                      negotiation.negotiationId,
                      "offeredPrice",
                      e.target.value
                    )
                  }
                />
                <input
                  type="text"
                  placeholder="Delivery Time"
                  value={supplierResponses[negotiation.negotiationId]?.deliveryTime || ""}
                  onChange={(e) =>
                    handleInputChange(
                      negotiation.negotiationId,
                      "deliveryTime",
                      e.target.value
                    )
                  }
                />
                <textarea
                  placeholder="Response Status"
                  value={supplierResponses[negotiation.negotiationId]?.responseStatus || ""}
                  onChange={(e) =>
                    handleInputChange(negotiation.negotiationId, "responseStatus", e.target.value)
                  }
                />
                <button onClick={() => handleSendResponse(negotiation)}>Send Response</button>
              </div>
            </div>
          ))}
        </Slider>
      )}
    </div>
  );
};

export default UserNegotiations;
